// src/components/common/PhotoLikeButton.tsx
import React, { useState } from 'react';
import {
   IconButton,
   Typography,
   Box,
   Tooltip,
   CircularProgress,
   Snackbar,
   Alert,
} from '@mui/material';
import {
   Favorite as FavoriteIcon,
   FavoriteBorder as FavoriteBorderIcon,
} from '@mui/icons-material';
import { PhotoLikeService } from '../../services/photoLikeService';

interface PhotoLikeButtonProps {
   photoId: string;
   userId: string;
   initialLikeCount?: number;
   initialIsLiked?: boolean;
   size?: 'small' | 'medium' | 'large';
   color?: 'primary' | 'secondary' | 'error' | 'warning' | 'info' | 'success';
   showCount?: boolean;
   disabled?: boolean;
   onLikeChange?: (isLiked: boolean, likeCount: number) => void;
   variant?: 'default' | 'compact' | 'detailed';
   // Context information for contextual likes
   tripId?: string;
   eventId?: string;
   albumId?: string;
   // New contextual like support
   context?: { type: 'collection' | 'trip' | 'event' | 'album' | 'original'; id?: string };
}

const PhotoLikeButton: React.FC<PhotoLikeButtonProps> = ({
   photoId,
   userId,
   initialLikeCount = 0,
   initialIsLiked = false,
   size = 'medium',
   color = 'error',
   showCount = true,
   disabled = false,
   onLikeChange,
   variant = 'default',
   tripId,
   eventId,
   albumId,
   context,
}) => {
   console.log(
      'PhotoLikeButton rendered for photo:',
      photoId,
      'user:',
      userId,
      'likeCount:',
      initialLikeCount,
      'isLiked:',
      initialIsLiked
   );
   const [likeCount, setLikeCount] = useState(initialLikeCount);
   const [isLiked, setIsLiked] = useState(initialIsLiked);
   const [isLoading, setIsLoading] = useState(false);
   const [error, setError] = useState<string | null>(null);

   const handleToggleLike = async (event: React.MouseEvent) => {
      event.preventDefault();
      event.stopPropagation();

      if (!userId || disabled || isLoading) return;

      setIsLoading(true);
      setError(null);

      try {
         // Create context object for contextual likes
         const likeContext = context || {
            tripId,
            eventId,
            albumId,
         };

         const response = await PhotoLikeService.toggleLike(photoId, userId, isLiked, likeContext);

         setIsLiked(response.isLiked);
         setLikeCount(response.likeCount);

         // Notify parent component of the change
         if (onLikeChange) {
            onLikeChange(response.isLiked, response.likeCount);
         }
      } catch (error: any) {
         console.error('Error toggling like:', error);
         setError(error.message || 'Failed to update like');
      } finally {
         setIsLoading(false);
      }
   };

   const handleCloseError = () => {
      setError(null);
   };

   const getIconSize = () => {
      switch (size) {
         case 'small':
            return 'small' as const;
         case 'large':
            return 'large' as const;
         default:
            return 'medium' as const;
      }
   };

   const getButtonSize = () => {
      switch (size) {
         case 'small':
            return 'small' as const;
         case 'large':
            return 'large' as const;
         default:
            return 'medium' as const;
      }
   };

   if (variant === 'compact') {
      return (
         <>
            <Box
               sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  cursor: disabled ? 'default' : 'pointer',
                  opacity: disabled ? 0.6 : 1,
               }}
               onClick={handleToggleLike}
            >
               {isLoading ? (
                  <CircularProgress size={16} />
               ) : (
                  <>
                     {isLiked ? (
                        <FavoriteIcon
                           fontSize={size === 'small' ? 'small' : 'medium'}
                           color={color}
                        />
                     ) : (
                        <FavoriteBorderIcon
                           fontSize={size === 'small' ? 'small' : 'medium'}
                           color='action'
                        />
                     )}
                     {showCount && (
                        <Typography
                           variant={size === 'small' ? 'caption' : 'body2'}
                           color='text.secondary'
                        >
                           {likeCount || 0}
                        </Typography>
                     )}
                  </>
               )}
            </Box>

            <Snackbar
               open={!!error}
               autoHideDuration={3000}
               onClose={handleCloseError}
               anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
               <Alert onClose={handleCloseError} severity='error' sx={{ width: '100%' }}>
                  {error}
               </Alert>
            </Snackbar>
         </>
      );
   }

   if (variant === 'detailed') {
      return (
         <>
            <Box
               sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  p: 1,
                  borderRadius: 1,
                  bgcolor: isLiked ? 'error.50' : 'transparent',
                  border: 1,
                  borderColor: isLiked ? 'error.main' : 'divider',
                  cursor: disabled ? 'default' : 'pointer',
                  opacity: disabled ? 0.6 : 1,
                  transition: 'all 0.2s ease',
                  '&:hover': disabled
                     ? {}
                     : {
                          bgcolor: isLiked ? 'error.100' : 'action.hover',
                       },
               }}
               onClick={handleToggleLike}
            >
               {isLoading ? (
                  <CircularProgress size={20} />
               ) : (
                  <>
                     {isLiked ? (
                        <FavoriteIcon fontSize={getIconSize()} color={color} />
                     ) : (
                        <FavoriteBorderIcon fontSize={getIconSize()} color='action' />
                     )}
                     <Box>
                        <Typography variant='body2' fontWeight={isLiked ? 'bold' : 'normal'}>
                           {isLiked ? 'Liked' : 'Like'}
                        </Typography>
                        {showCount && (
                           <Typography variant='caption' color='text.secondary'>
                              {likeCount} {likeCount === 1 ? 'like' : 'likes'}
                           </Typography>
                        )}
                     </Box>
                  </>
               )}
            </Box>

            <Snackbar
               open={!!error}
               autoHideDuration={3000}
               onClose={handleCloseError}
               anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
               <Alert onClose={handleCloseError} severity='error' sx={{ width: '100%' }}>
                  {error}
               </Alert>
            </Snackbar>
         </>
      );
   }

   // Default variant
   return (
      <>
         <Tooltip title={isLiked ? 'Unlike' : 'Like'}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
               <IconButton
                  onClick={handleToggleLike}
                  disabled={disabled || isLoading}
                  size={getButtonSize()}
                  sx={{
                     color: isLiked ? `${color}.main` : 'action.disabled',
                     '&:hover': {
                        color: `${color}.main`,
                        bgcolor: `${color}.50`,
                     },
                  }}
               >
                  {isLoading ? (
                     <CircularProgress size={20} />
                  ) : isLiked ? (
                     <FavoriteIcon fontSize={getIconSize()} />
                  ) : (
                     <FavoriteBorderIcon fontSize={getIconSize()} />
                  )}
               </IconButton>

               {showCount && (
                  <Typography
                     variant={size === 'small' ? 'caption' : 'body2'}
                     sx={{
                        color: '#000000',
                        fontWeight: 'bold',
                        minWidth: '20px',
                        textAlign: 'center',
                        fontSize: size === 'small' ? '0.875rem' : '1rem',
                        backgroundColor: 'rgba(255, 255, 255, 0.9)',
                        borderRadius: '4px',
                        px: 0.5,
                        py: 0.25,
                     }}
                  >
                     {likeCount || 0}
                  </Typography>
               )}
            </Box>
         </Tooltip>

         <Snackbar
            open={!!error}
            autoHideDuration={3000}
            onClose={handleCloseError}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
         >
            <Alert onClose={handleCloseError} severity='error' sx={{ width: '100%' }}>
               {error}
            </Alert>
         </Snackbar>
      </>
   );
};

export default PhotoLikeButton;
